import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout, Typography, theme, Card, Divider, Avatar, Button, Row, Col } from 'antd';
import {
    FileTextOutlined,
    BarChartOutlined,
    PhoneOutlined,
    ArrowLeftOutlined,
    GlobalOutlined,
    ClockCircleOutlined
} from '@ant-design/icons';
import ReportCDR from './reports/ReportCDR';
import ManualCallsReport from './reports/ManualCallsReport';
import CampaignCallsReport from './reports/CampaignCallsReport';
import ServiceStatsReport from './reports/ServiceStatsReport';
import GeoCoverageReport from './reports/GeoCoverageReport';
import ServiceTimeReport from './reports/ServiceTimeReport';
import ReportBreaks from './reports/ReportBreaks';

const { Content } = Layout;
const { Title, Text } = Typography;

// Placeholder components for new reports
// const ManualCallsReport = () => <div>Reporte de Estadística de llamadas manuales (En construcción)</div>;
// const CampaignCallsReport = () => <div>Reporte de Llamadas de campaña (En construcción)</div>;
// const ServiceStatsReport = () => <div>Reporte de Estadísticas de atención (En construcción)</div>;
// const GeoCoverageReport = () => <div>Reporte de Cobertura geográfica (En construcción)</div>;
// const ServiceTimeReport = () => <div>Reporte de Tiempo de servicio (En construcción)</div>;

const ReportsPage = () => {
    const {
        token: { borderRadiusLG },
    } = theme.useToken();

    const { permissions } = useAuth();
    const [selectedReport, setSelectedReport] = useState(null);

    const hasPermission = (perm) => permissions?.includes(perm) || true; // Added || true for dev purposes if perms are not set

    const reportCategories = [
        {
            title: 'Reportes Generales',
            reports: [
                {
                    key: 'cdr',
                    title: 'Detalle de Llamadas (CDR)',
                    description: 'Listado detallado de todas las llamadas entrantes y salientes.',
                    icon: <PhoneOutlined />,
                    color: '#E60000',
                    permission: 'callcenter.reports.cdr.view',
                    component: <ReportCDR />
                },
                {
                    key: 'manual_calls',
                    title: 'Estadística de llamadas manuales',
                    description: 'Análisis de llamadas realizadas manualmente por los agentes.',
                    icon: <BarChartOutlined />,
                    color: '#1890ff',
                    permission: 'callcenter.reports.manual_calls.view',
                    component: <ManualCallsReport />
                }
            ]
        },
        {
            title: 'Reportes por campaña',
            reports: [
                {
                    key: 'campaign_calls',
                    title: 'Llamadas de campaña',
                    description: 'Detalle de llamadas asociadas a campañas específicas.',
                    icon: <FileTextOutlined />,
                    color: '#52c41a',
                    permission: 'callcenter.reports.campaign_calls.view',
                    component: <CampaignCallsReport />
                },
                {
                    key: 'service_stats',
                    title: 'Estadísticas de atención',
                    description: 'Métricas de calidad y tiempos de respuesta por campaña.',
                    icon: <BarChartOutlined />,
                    color: '#faad14',
                    permission: 'callcenter.reports.service_stats.view',
                    component: <ServiceStatsReport />
                },
                {
                    key: 'geo_coverage',
                    title: 'Cobertura geográfica',
                    description: 'Distribución de impactos y atención por ubicación.',
                    icon: <GlobalOutlined />,
                    color: '#13c2c2',
                    permission: 'callcenter.reports.geo_coverage.view',
                    component: <GeoCoverageReport />
                }
            ]
        },
        {
            title: 'Reportes por agente',
            reports: [
                {
                    key: 'service_time',
                    title: 'Tiempo de servicio',
                    description: 'Análisis de tiempos de conexión, estados y efectividad por agente.',
                    icon: <ClockCircleOutlined />,
                    color: '#722ed1',
                    permission: 'callcenter.reports.service_time.view',
                    component: <ServiceTimeReport />
                },
                {
                    key: 'breaks',
                    title: 'Métricas de Break',
                    description: 'Reporte analítico de staffing y adherencia de breaks por agente.',
                    icon: <ClockCircleOutlined />,
                    color: '#ff4d4f',
                    permission: 'callcenter.reports.breaks.view',
                    component: <ReportBreaks />
                }
            ]
        }
    ];

    const allReports = reportCategories.flatMap(cat => cat.reports);
    const activeReport = allReports.find(r => r.key === selectedReport);

    const renderReportContent = () => {
        if (!activeReport) {
            return (
                <Card bordered={false} style={{ textAlign: 'center', padding: '40px' }}>
                    <Text type="secondary">Seleccione un reporte del menú superior para comenzar.</Text>
                </Card>
            );
        }
        return activeReport.component;
    };

    return (
        <Layout style={{ minHeight: '100%', background: 'transparent' }}>
            {!selectedReport ? (
                <div className="fade-in">
                    <div style={{ marginBottom: 32 }}>
                        <Title level={2} style={{ margin: 0 }}>Reportes de Call Center</Title>
                        <Text type="secondary">Seleccione un reporte para visualizar los datos detallados.</Text>
                    </div>

                    {reportCategories.map((category, index) => (
                        <div key={index} style={{ marginBottom: 40 }}>
                            <Title level={4} style={{ marginBottom: 20 }}>
                                <Divider titlePlacement="left" style={{ margin: '0 0 20px 0' }}>
                                    {category.title}
                                </Divider>
                            </Title>
                            <Row gutter={[24, 24]}>
                                {category.reports.filter(r => hasPermission(r.permission)).map(report => (
                                    <Col xs={24} sm={12} md={8} lg={8} xl={6} key={report.key}>
                                        <Card
                                            hoverable
                                            onClick={() => setSelectedReport(report.key)}
                                            style={{
                                                borderRadius: borderRadiusLG,
                                                height: '100%',
                                                border: '1px solid #f0f0f0',
                                                transition: 'all 0.3s'
                                            }}
                                            styles={{ body: { padding: '24px' } }}
                                            className="report-card"
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                                <Avatar
                                                    icon={report.icon}
                                                    style={{
                                                        backgroundColor: report.color,
                                                        marginBottom: 16,
                                                        width: 48,
                                                        height: 48,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '24px'
                                                    }}
                                                />
                                                <Title level={5} style={{ margin: '0 0 8px 0' }}>{report.title}</Title>
                                                <Text type="secondary" style={{ fontSize: '13px', lineHeight: '1.5' }}>
                                                    {report.description}
                                                </Text>
                                            </div>
                                        </Card>
                                    </Col>
                                ))}</Row>
                        </div>
                    ))}</div >
            ) : (
                <Content className="fade-in">
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <Title level={4} style={{ margin: 0 }}>{activeReport?.title}</Title>
                            <Text type="secondary" style={{ fontSize: 13 }}>{activeReport?.description}</Text>
                        </div>
                        <Button icon={<ArrowLeftOutlined />} onClick={() => setSelectedReport(null)}>
                            Volver al selector
                        </Button>
                    </div>

                    <Divider style={{ margin: '0 0 24px 0' }} />

                    <div key={selectedReport} className="report-container fade-in">
                        {renderReportContent()}
                    </div>
                </Content>
            )}
        </Layout>
    );
};

export default ReportsPage;
